import { FileTextOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Collapse, CollapsePanelProps, List, Tooltip, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory } from 'react-router-dom'

import { ProjectProgress, RiskBar } from '@/components'
import { routes } from '@/router'
import { Project, ReportBase } from '@/schemas'
import { ga, traceEvent } from '@/utils/ga'

import { PlanDate } from './plan-date.component'
import { Switch } from './switch.component'

const { Text } = Typography

interface MyProject extends Project {
  thisWeekReport: ReportBase
}

export const MyProjects: React.FC<CollapsePanelProps> = (props) => {
  const { t } = useTranslation()
  const history = useHistory()
  const [searchType, setSearchType] = React.useState(1)

  const { data } = useRequest<{ list: MyProject[]; total: number }>(
    {
      url: '/api/project/getMyProjects',
      params: {
        page: 1,
        size: 100,
        searchType, // 1 负责, 2 参与
      },
    },
    {
      refreshDeps: [searchType],
    }
  )

  const options = [
    { label: t('owner'), value: 1, name: '负责' },
    { label: t('join in'), value: 2, name: '参与' },
  ]

  const onChange = (searchType: number) => {
    setSearchType(searchType)
  }

  const header = (
    <>
      <span>{props.header}</span>
      <Switch options={options} value={searchType} onChange={onChange} />
    </>
  )

  return (
    <Collapse.Panel {...props} header={header}>
      <List
        className='max-h-600px overflow-y-auto'
        itemLayout='horizontal'
        size='small'
        bordered
        loading={!data}
        locale={{
          emptyText: (
            <div className='text-left'>
              {/* t('personal.myproject.tip') t('personal.memberproject.tip') */}
              {t(
                options.find((o) => o.value === searchType)!.name === '负责'
                  ? 'personal.myproject.tip'
                  : 'personal.memberproject.tip'
              )}
            </div>
          ),
        }}
        dataSource={data?.list || []}
        renderItem={(p) => (
          <List.Item className='space-x-24px'>
            <Text className='flex-grow flex items-stretch'>
              <RiskBar className='mr-20px' risk={p.risk} />
              <Link
                to={routes.project.page(p.selfId)}
                onClick={() => {
                  ga('event', traceEvent.personalPage.events.click_check_detail, {
                    event_category:
                      searchType === 1
                        ? traceEvent.personalPage.event_categories.ef_pmo_home_my_projects_owner
                        : traceEvent.personalPage.event_categories.ef_pmo_home_my_projects_member,
                    action: traceEvent.personalPage.action,
                    event_label: p.name,
                    target: traceEvent.personalPage.targets.ef_pmo_project_detail,
                  })

                  window.sessionStorage.setItem('entry', traceEvent.detailPage.event_categories.ef_pmo_home)
                }}
              >
                {p.name}
              </Link>
            </Text>

            {p.status === 1 && (
              <Tooltip title={p.thisWeekReport ? t('view report') : t('report.gowrite')}>
                <FileTextOutlined
                  className={p.thisWeekReport ? 'text-blue-600' : 'text-red-600'}
                  onClick={() => history.push(routes.project.page(p.selfId, { tab: 'report' }))}
                />
              </Tooltip>
            )}

            <ProjectProgress project={p} />

            <PlanDate date={p.endDate} tooltip={t('plan end date')} />
          </List.Item>
        )}
      />
    </Collapse.Panel>
  )
}
